import type { Prisma, PrismaClient } from '@prisma/client';
import { HttpError } from '../../core/errors.js';

type Db = PrismaClient | Prisma.TransactionClient;

export const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`);
export const isoDate = (d: Date) => d.toISOString().slice(0, 10);

/** Creates one open fiscal period per calendar month of a financial year (idempotent). */
export async function createPeriodsForYear(
  db: Db,
  companyId: number,
  year: { id: number; startDate: Date; endDate: Date },
): Promise<void> {
  let cursor = new Date(Date.UTC(year.startDate.getUTCFullYear(), year.startDate.getUTCMonth(), 1));
  while (cursor <= year.endDate) {
    const start = new Date(cursor);
    const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
    const name = `${start.getUTCFullYear()}-${String(start.getUTCMonth() + 1).padStart(2, '0')}`;
    await db.fiscalPeriod.upsert({
      where: { companyId_name: { companyId, name } },
      create: { companyId, financialYearId: year.id, name, startDate: start, endDate: end },
      update: {},
    });
    cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  }
}

/** Posting is only allowed into an open period (PLAN.md section 8, rule 4). */
export async function assertPeriodOpen(db: Db, companyId: number, date: Date): Promise<void> {
  const period = await db.fiscalPeriod.findFirst({
    where: { companyId, startDate: { lte: date }, endDate: { gte: date } },
  });
  if (!period) {
    throw new HttpError(
      422,
      'NO_PERIOD',
      `No fiscal period covers ${isoDate(date)}. Create the financial year in Settings first.`,
    );
  }
  if (period.status !== 'open') {
    throw new HttpError(
      422,
      'PERIOD_CLOSED',
      `The period ${period.name} is closed. An Admin must reopen it (with a reason) before posting.`,
    );
  }
}
